import { loadAssetPreviewFrames } from '../app/asset_preview_orientation.mjs';
import { applyGhostStyle } from '../../plugin-sdk/ghost_preview.mjs';
// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import {
  normalizeAngle,
  rotateWorldPoint,
  snap
} from '../utils/common_constants.js?v=20260706d';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import {
  getPlanSvg,
  screenToWorld,
  setStatus,
  svgEl,
  worldToScreen
} from '../render/plan_renderer.js?v=20260904f';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import {
  clearDrawingObjects,
  registerDrawingObject
} from './drawing_object_registry.js?v=20260904f';
import {
  clearDrawingSelection,
  setDrawingSelection
} from '../selection/drawing_selection_manager.js?v=20260706d';
import {
  drawImportedSvgAsset,
  loadImportedSvgAsset
} from './imported_svg_asset.js?v=20260831c';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import { setInteractionFlag } from '../interaction/interaction_transient_state.js?v=20260904f';
import {
  OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION,
  normalizedObjectAssetFloorOffsetMm,
  normalizedObjectAssetModelRotationOffsetDeg,
  objectAssetModelRotationOffsetRad,
  objectAssetPlacementDefaults
} from './object_asset_defaults.js?v=20260730a';
import { computeDimensionSnapMetadata } from '../services/dimension_geometry_client.js?v=20260812a';

const OBJECT_ASSET_SYSTEM_ID = 'core_object_assets';
const OBJECT_ASSET_PROJECT_KEY = 'objectAssets';
const DEFAULT_OBJECT_SIZE = 800;
const OBJECT_ASSET_SVG_CACHE = new Map();
const OBJECT_ASSET_DXF_ENTITIES_CACHE = new Map();
const ASSET_CACHE = {
  loaded: false,
  loading: false,
  error: '',
  items: []
};
let objectAssetRender3DModulePromise = null;

function ensureUi() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  return S.ui;
}

function ensureSelection() {
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!Array.isArray(S.selection.objectAssetIds)) S.selection.objectAssetIds = [];
  return S.selection;
}

function ensureObjectAssets(project = S.project) {
  if (!project || typeof project !== 'object') return [];
  if (!Array.isArray(project[OBJECT_ASSET_PROJECT_KEY])) project[OBJECT_ASSET_PROJECT_KEY] = [];
  const list = project[OBJECT_ASSET_PROJECT_KEY];
  const next = [];
  for (const raw of list) {
    const normalized = normalizeObjectAsset(raw);
    if (!normalized) continue;
    const migrated = migrateObjectAssetToV2Library(normalized, ASSET_CACHE.items);
    // Bestehende Objekt-Referenz in-place mutieren statt durch eine neue
    // Instanz zu ersetzen -- objectAssets() ruft ensureObjectAssets() bei
    // JEDEM Aufruf auf (sehr haeufig, u.a. bei jedem Plan-Render/Hit-Test).
    // Ein Ersetzen der Instanz liess jede anderswo kurzfristig gehaltene
    // Objekt-Referenz sofort veralten -- konkret brach das den generischen
    // Dreh-Griff-Drag (core-group-rotate-handle in selection_controller.js):
    // dessen bei Drag-Start erfasste getState()-Closure las weiterhin vom
    // alten, verwaisten Objekt und sah nie die eigenen Zwischenschritte, der
    // Griff sprang beim Loslassen zurueck (Nutzer-Meldung 2026-08-18).
    Object.assign(raw, migrated);
    next.push(raw);
  }
  project[OBJECT_ASSET_PROJECT_KEY] = next;
  return next;
}

function normalizeScale(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 1;
  return Math.max(0.05, Math.min(20, numeric));
}

function normalizeObjectAsset(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim() || makeId('object_asset');
  const label = String(raw.label || raw.name || raw.articleNumber || raw.assetFile || 'Objekt').trim() || 'Objekt';
  // Alte Projekte kannten nur einen einzigen `scale`-Multiplikator statt
  // echter mm-Werte pro Achse. Fehlen width/height/depth komplett, aber ein
  // legacy `scale` ist vorhanden, bleibt die bisherige Grösse erhalten statt
  // auf die neue Originalgrösse zu springen.
  const legacyScale = raw.width == null && raw.height == null && raw.depth == null && raw.scale != null
    ? normalizeScale(raw.scale)
    : 1;
  const width = Number(raw.width ?? raw.baseWidth ?? raw.defaultWidth);
  const height = Number(raw.height ?? raw.baseHeight ?? raw.defaultHeight);
  const depth = Number(raw.depth ?? raw.baseDepth ?? raw.defaultDepth);
  const finalWidth = Number.isFinite(width) && width > 0 ? width : DEFAULT_OBJECT_SIZE * legacyScale;
  const finalDepth = Number.isFinite(depth) && depth > 0 ? depth : DEFAULT_OBJECT_SIZE * legacyScale;
  const finalHeight = Number.isFinite(height) && height > 0 ? height : DEFAULT_OBJECT_SIZE * legacyScale;
  const sourceWidth = Number(raw.sourceWidth);
  const sourceDepth = Number(raw.sourceDepth);
  const sourceHeight = Number(raw.sourceHeight);
  const assetObjectId = Number(raw.assetObjectId);
  return {
    id,
    type: String(raw.type || 'object_asset'),
    category: String(raw.category || 'Objekte').trim() || 'Objekte',
    assetCategory: 'objects',
    sourceType: String(raw.sourceType || '').trim(),
    x: Number.isFinite(Number(raw.x)) ? Number(raw.x) : 0,
    y: normalizedObjectAssetFloorOffsetMm(raw, raw.y, raw.placementDefaultsVersion),
    z: Number.isFinite(Number(raw.z)) ? Number(raw.z) : 0,
    rotation: normalizeAngle(Number(raw.rotation) || 0),
    label,
    name: String(raw.name || label).trim() || label,
    color: String(raw.color || '').trim(),
    assetFile: String(raw.assetFile || '').trim(),
    modelPath: String(raw.modelPath || raw.glbPath || '').trim(),
    planStrokeWidthFactor: Number(raw.planStrokeWidthFactor) || 1,
    svgPath: String(raw.svgPath || '').trim(),
    stepPath: String(raw.stepPath || '').trim(),
    dxfPath: String(raw.dxfPath || '').trim(),
    // dimension_geometry.json (STEP-Kanten/-Mittelpunkte der Draufsicht) +
    // die Originalgroesse, auf die deren mm-Koordinaten bezogen sind --
    // siehe objectAssetDimensionSnapMetadata(). Optional: fehlt bei
    // v1.0-Assets und alten, vor dieser Ergaenzung konvertierten v2.0-Objekten.
    dimensionGeometryPath: String(raw.dimensionGeometryPath || '').trim(),
    dxfEntitiesPath: String(raw.dxfEntitiesPath || '').trim(),
    wireframeDxfPath: String(raw.wireframeDxfPath || '').trim(),
    assetObjectId: Number.isInteger(assetObjectId) && assetObjectId > 0 ? assetObjectId : null,
    // Fuer die STEP-Export-Teilebeschriftung (objectAssetStepExportItems()).
    articleNumber: String(raw.articleNumber || '').trim(),
    sourceWidth: Number.isFinite(sourceWidth) && sourceWidth > 0 ? sourceWidth : finalWidth,
    sourceDepth: Number.isFinite(sourceDepth) && sourceDepth > 0 ? sourceDepth : finalDepth,
    sourceHeight: Number.isFinite(sourceHeight) && sourceHeight > 0 ? sourceHeight : finalHeight,
    width: finalWidth,
    height: finalHeight,
    depth: finalDepth,
    modelRotationOffsetDeg: normalizedObjectAssetModelRotationOffsetDeg(
      raw,
      raw.modelRotationOffsetDeg,
      raw.placementDefaultsVersion
    ),
    placementDefaultsVersion: OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION,
    hidden: raw.hidden === true
  };
}

function objectAssets() {
  return ensureObjectAssets(S.project);
}

function objectAssetById(id) {
  return objectAssets().find(item => item.id === id) || null;
}

function selectedObjectAssetIds() {
  return [...new Set(
    SelectionService.items()
      .filter(item => item.owner === OBJECT_ASSET_SYSTEM_ID && item.kind === 'object_asset')
      .map(item => item.id)
      .filter(id => Boolean(objectAssetById(id)))
  )];
}

function selectedObjectAsset() {
  return objectAssetById(selectedObjectAssetIds()[0]);
}

function objectAssetDimensions(item) {
  return {
    width: Number(item?.width) || DEFAULT_OBJECT_SIZE,
    height: Number(item?.height) || DEFAULT_OBJECT_SIZE,
    depth: Number(item?.depth) || DEFAULT_OBJECT_SIZE
  };
}

function objectAssetCorners(item) {
  const { width, depth } = objectAssetDimensions(item);
  const center = { x: Number(item.x) || 0, z: Number(item.z) || 0 };
  return [
    { x: center.x - width / 2, z: center.z - depth / 2 },
    { x: center.x + width / 2, z: center.z - depth / 2 },
    { x: center.x + width / 2, z: center.z + depth / 2 },
    { x: center.x - width / 2, z: center.z + depth / 2 }
  ].map(point => rotateWorldPoint(point, center, item.rotation || 0));
}

function objectAssetBounds(item) {
  const corners = objectAssetCorners(item);
  return {
    minX: Math.min(...corners.map(point => point.x)),
    maxX: Math.max(...corners.map(point => point.x)),
    minZ: Math.min(...corners.map(point => point.z)),
    maxZ: Math.max(...corners.map(point => point.z))
  };
}

function objectAssetLocalBounds(item) {
  const { width, depth } = objectAssetDimensions(item);
  const center = { x: Number(item?.x) || 0, z: Number(item?.z) || 0 };
  return {
    minX: center.x - width / 2,
    minZ: center.z - depth / 2,
    maxX: center.x + width / 2,
    maxZ: center.z + depth / 2
  };
}

function getObjectAssetCoreObjects() {
  const selected = new Set(selectedObjectAssetIds());
  return objectAssets().map(item => ({
    id: item.id,
    objectId: item.id,
    objectType: 'object_asset',
    name: objectAssetDisplayName(item),
    label: objectAssetDisplayName(item),
    systemId: OBJECT_ASSET_SYSTEM_ID,
    productLabel: 'Objekte',
    bounds: objectAssetBounds(item),
    omitFromBom: true,
    rotationDeg: Number(item.rotation) || 0,
    rotationPivot: { x: Number(item.x) || 0, z: Number(item.z) || 0 },
    visible: item.hidden !== true,
    active: selected.has(item.id),
    raw: item
  }));
}

function pointInObjectAsset(point, item) {
  const { width, depth } = objectAssetDimensions(item);
  const local = rotateWorldPoint(point, { x: item.x, z: item.z }, -(Number(item.rotation) || 0));
  return local.x >= item.x - width / 2
    && local.x <= item.x + width / 2
    && local.z >= item.z - depth / 2
    && local.z <= item.z + depth / 2;
}

function clearObjectAssetSelection() {
  const selection = ensureSelection();
  selection.objectAssetId = null;
  selection.objectAssetIds = [];
  if (Array.isArray(selection.items)) {
    selection.items = selection.items.filter(item => item.owner !== OBJECT_ASSET_SYSTEM_ID);
  }
  clearDrawingSelection({ emit: false });
}

function selectObjectAssets(ids = [], options = {}) {
  const normalizedIds = [...new Set((Array.isArray(ids) ? ids : [ids]).filter(id => objectAssetById(id)))];
  const selection = ensureSelection();
  if (!normalizedIds.length) {
    clearObjectAssetSelection();
    return false;
  }
  if (options.clearForeign !== false) {
    SelectionService.clearSystemSelectionsExcept(OBJECT_ASSET_SYSTEM_ID);
  }
  selection.objectAssetId = normalizedIds[0] || null;
  selection.objectAssetIds = [...normalizedIds];
  selection.items = (selection.items || []).filter(item => item.owner !== OBJECT_ASSET_SYSTEM_ID);
  for (const id of normalizedIds) {
    selection.items = [...selection.items, { owner: OBJECT_ASSET_SYSTEM_ID, kind: 'object_asset', id }];
  }
  ensureUi().showObjectLibraryPanel = true;
  ensureUi().showAppOptions = false;
  ensureUi().showAppAdminPanel = false;
  setDrawingSelection(normalizedIds);
  return true;
}

function updateObjectAsset(id, patch = {}, options = {}) {
  const item = objectAssetById(id);
  if (!item) return null;
  Object.assign(item, normalizeObjectAsset({ ...item, ...patch, id: item.id }));
  if (options.refresh !== false) AppRuntime.refresh(false);
  return item;
}

function removeObjectAssets(ids = selectedObjectAssetIds()) {
  const idSet = new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean));
  if (!idSet.size) return 0;
  const items = objectAssets();
  const before = items.length;
  S.project[OBJECT_ASSET_PROJECT_KEY] = items.filter(item => !idSet.has(item.id));
  clearObjectAssetSelection();
  clearDrawingObjects({ systemType: OBJECT_ASSET_SYSTEM_ID });
  return before - S.project[OBJECT_ASSET_PROJECT_KEY].length;
}

function assetLibraryItems() {
  return ASSET_CACHE.items;
}

function objectAssetLibraryError() {
  return ASSET_CACHE.error;
}

async function refreshObjectAssetLibrary(options = {}) {
  if (ASSET_CACHE.loading) return ASSET_CACHE.items;
  if (ASSET_CACHE.loaded && options.force !== true) return ASSET_CACHE.items;
  ASSET_CACHE.loading = true;
  ASSET_CACHE.error = '';
  try {
    const v2Items = await fetchAssetObjectsV2Library();
    ASSET_CACHE.items = v2Items;
    ASSET_CACHE.loaded = true;
    ensureObjectAssets(S.project);
    AppRuntime.requestAppRefresh(false, 'object-assets-v2-library-ready');
  } catch (error) {
    ASSET_CACHE.error = error?.message || 'Objekt-Assets konnten nicht geladen werden.';
  } finally {
    ASSET_CACHE.loading = false;
  }
  return ASSET_CACHE.items;
}

// Asset Import 2.0 (docs/adr/0001-occt-asset-import-pipeline.md): Objekte
// aus der neuen, DB-gestuetzten OCCT-Pipeline (`asset_objects`, Kind
// 'decoration' = global/nicht systemgebunden) werden in dieselbe flache
// Bibliotheks-Item-Form gebracht, damit
// beginObjectAssetPlacement()/createPlacedObjectAsset()/drawObjectAsset()/
// buildObjectAsset3D() unveraendert wiederverwendet werden koennen — sie
// arbeiten nur mit dieser Struktur, nicht mit der Datenherkunft. Die
// Bibliothek hat keinen v1-Fallback mehr; ein Ladefehler wird deshalb im
// Bibliothekspanel ausgewiesen statt mit einer vermeintlich leeren
// Bibliothek verwechselt.
async function fetchAssetObjectsV2Library() {
  const response = await fetch('/api/asset-objects?assetKind=decoration', { cache: 'no-store' });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.ok === false) {
    throw new Error(result.error || 'Objekt-Assets konnten nicht geladen werden.');
  }
  const items = Array.isArray(result.items) ? result.items : [];
  return Promise.all(items.filter(row => row.conversionStatus === 'succeeded').map(async row => {
    const item = normalizeAssetObjectV2LibraryItem(row);
    if (!item) return null;
    try {
      const frames = await loadAssetPreviewFrames(row);
      const frame = frames?.top;
      item.thumbnailPath = frame
        ? `${item.svgPath}${item.svgPath.includes('?') ? '&' : '?'}view=${encodeURIComponent(frame.artifactView)}`
        : item.svgPath;
      item.thumbnailTransform = frame?.transform || [1, 0, 0, 1, 0, 0];
      item.thumbnailLegacy = !frame;
    } catch { item.thumbnailPath = ''; }
    return item;
  })).then(entries => entries.filter(Boolean));
}

function normalizeAssetObjectV2LibraryItem(row = {}) {
  const rawId = row.id;
  if (rawId == null) return null;
  const id = `v2:${rawId}`;
  const label = String(row.displayName || row.articleNumber || id).trim() || id;
  const bbox = row.boundingBox && typeof row.boundingBox === 'object' ? row.boundingBox : null;
  // STEP ist Z-up (Boden=XY, Hoehe=Z), das GLB wird beim Export nach glTFs
  // Y-up konvertiert (siehe cad-converter glb_export.py) -> width=Welt-X,
  // height=Welt-Z, depth=Welt-Y (empirisch verifiziert, siehe cad-converter-
  // Testsuite test_glb_export_converts_step_z_up_to_gltf_y_up).
  const width = bbox ? Number(bbox.max?.x) - Number(bbox.min?.x) : NaN;
  const height = bbox ? Number(bbox.max?.z) - Number(bbox.min?.z) : NaN;
  const depth = bbox ? Number(bbox.max?.y) - Number(bbox.min?.y) : NaN;
  // Fingerprint-Query (?v=updatedAt): svg/glb sind MUTIERBAR unter
  // derselben URL (Farb-Override ueberschreibt die Datei, siehe
  // asset_objects.js/handleUpdateColorOverride) -- ohne diesen Parameter
  // wuerden sowohl der Browser-HTTP-Cache als auch der In-Memory-Modell-
  // Cache in three_asset_loader.js (Map, per URL-String) nach einer
  // Farbaenderung weiter die alte Datei liefern (gemeldeter Bug: SVG zeigt
  // die neue Farbe, GLB im 3D-Viewer noch die alte). updatedAt aendert sich
  // bei jeder Aktualisierung automatisch, aendert also zuverlaessig die URL.
  const cacheBust = row.updatedAt ? `?v=${encodeURIComponent(row.updatedAt)}` : '';
  const placementDefaults = objectAssetPlacementDefaults({
    id,
    articleNumber: row.articleNumber,
    name: label,
    label
  });
  return {
    id,
    assetObjectId: Number(rawId),
    planStrokeWidthFactor: Number(row.planStrokeWidthFactor) || 1,
    label,
    name: label,
    category: 'Objekte',
    assetFile: '',
    svgPath: `/api/asset-objects/${rawId}/file/svg${cacheBust}`,
    modelPath: `/api/asset-objects/${rawId}/file/glb${cacheBust}`,
    stepPath: `/api/asset-objects/${rawId}/file/source${cacheBust}`,
    dxfPath: `/api/asset-objects/${rawId}/file/dxf2d${cacheBust}`,
    // Vom Bemassungswerkzeug konsumierte echte Kanten/Mittelpunkte der
    // Draufsicht-Projektion (dimension_geometry.py) -- siehe
    // objectAssetDimensionSnapMetadata(). Fehlt bei alten, vor dieser
    // Ergaenzung konvertierten Objekten (Spalte dann NULL) -> Endpunkt
    // liefert 404, requestDimensionGeometry() faengt das ab.
    dimensionGeometryPath: `/api/asset-objects/${rawId}/file/dimensionGeometry${cacheBust}`,
    // Fuer den projektweiten DXF-Export (js/core/export/dxf_export.js):
    // echte LINE/CIRCLE/ARC-Entitaeten statt nur eines Bounding-Box-
    // Rechtecks -- siehe objectAssetDxfShapes(). Gleiche Optionalitaet wie
    // dimensionGeometryPath.
    dxfEntitiesPath: `/api/asset-objects/${rawId}/file/dxfEntities${cacheBust}`,
    // Fuer den projektweiten Wireframe-Export (view3d_controller.js
    // exportProjectWireframe()): das komplette 3D-Drahtmodell (STEP-nativ,
    // X=Breite/Y=Tiefe/Z=Hoehe) wird serverseitig (cad-converter,
    // wireframe_combine.py) zentriert/skaliert/rotiert/platziert -- der
    // Client liefert nur den rohen DXF-Text + Platzierungsparameter.
    wireframeDxfPath: `/api/asset-objects/${rawId}/file/dxfWireframe${cacheBust}`,
    color: '',
    description: String(row.articleNumber || '').trim(),
    // Fuer den projektweiten STEP-Export (objectAssetStepExportItems()):
    // Teilebeschriftung im kombinierten STEP soll die echte Artikelnummer
    // sein, nicht der generische Name aus der Quell-STEP-Datei (haeufig
    // schlicht "Product", je nach urspruenglichem Exporter-Tool).
    articleNumber: String(row.articleNumber || '').trim(),
    sourceType: 'step',
    width: Number.isFinite(width) && width > 0 ? width : undefined,
    height: Number.isFinite(height) && height > 0 ? height : undefined,
    depth: Number.isFinite(depth) && depth > 0 ? depth : undefined,
    // Referenzgroesse fuer die Skalierung der dimension_geometry.json-
    // Koordinaten (STEP-mm) auf die tatsaechliche, ggf. pro Platzierung
    // veraenderte Objektgroesse (item.width/item.depth) -- siehe
    // objectAssetDimensionSnapMetadata(). sourceHeight zusaetzlich fuer die
    // Z-Achsen-Skalierung im Wireframe-Export (objectAssetWireframeExportItems()).
    sourceWidth: Number.isFinite(width) && width > 0 ? width : undefined,
    sourceDepth: Number.isFinite(depth) && depth > 0 ? depth : undefined,
    sourceHeight: Number.isFinite(height) && height > 0 ? height : undefined,
    defaultFloorOffsetMm: placementDefaults?.floorOffsetMm || 0,
    modelRotationOffsetDeg: placementDefaults?.modelRotationOffsetDeg || 0,
    placementDefaultsVersion: OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION
  };
}

function normalizeObjectAssetLookupKey(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  let decoded = text;
  try {
    decoded = decodeURIComponent(text);
  } catch (_error) {
    // Bereits lesbarer Anzeigename oder Pfad.
  }
  const basename = decoded.split(/[\\/]/).at(-1)?.split(/[?#]/)[0] || decoded;
  return basename.replace(/\.(?:step|stp|svg|dxf|glb|json)$/i, '').trim().toLowerCase();
}

function objectAssetLookupKeys(value = {}) {
  const genericRouteSegments = new Set([
    'dxf2d',
    'dxfentities',
    'dxfwireframe',
    'dimensiongeometry',
    'glb',
    'source',
    'svg'
  ]);
  return [...new Set([
    value.articleNumber,
    value.assetFile,
    value.modelPath,
    value.svgPath,
    value.stepPath,
    value.name,
    value.label
  ].map(normalizeObjectAssetLookupKey).filter(key => key && !genericRouteSegments.has(key)))];
}

function objectAssetIdFromV2Path(value) {
  const match = String(value || '').match(/\/api\/asset-objects\/(\d+)\/file\//);
  return match ? Number(match[1]) : null;
}

function migrateObjectAssetToV2Library(item = {}, libraryItems = []) {
  if (!item || typeof item !== 'object') return item;
  const v2Items = (libraryItems || []).filter(asset => Number(asset?.assetObjectId) > 0);
  if (!v2Items.length) return item;
  const explicitAssetObjectId = Number(item.assetObjectId)
    || [item.modelPath, item.svgPath, item.stepPath, item.dxfPath]
      .map(objectAssetIdFromV2Path)
      .find(Boolean)
    || null;
  let matched = Number.isInteger(explicitAssetObjectId) && explicitAssetObjectId > 0
    ? v2Items.find(asset => asset.assetObjectId === explicitAssetObjectId)
    : null;
  if (!matched) {
    const itemKeys = new Set(objectAssetLookupKeys(item));
    matched = v2Items.find(asset => objectAssetLookupKeys(asset).some(key => itemKeys.has(key))) || null;
  }
  if (!matched) return item;
  return {
    ...item,
    category: 'Objekte',
    assetFile: '',
    modelPath: matched.modelPath || '',
    planStrokeWidthFactor: matched.planStrokeWidthFactor || 1,
    svgPath: matched.svgPath || '',
    stepPath: matched.stepPath || '',
    dxfPath: matched.dxfPath || '',
    dimensionGeometryPath: matched.dimensionGeometryPath || '',
    dxfEntitiesPath: matched.dxfEntitiesPath || '',
    wireframeDxfPath: matched.wireframeDxfPath || '',
    articleNumber: matched.articleNumber || item.articleNumber || '',
    sourceType: 'step',
    sourceWidth: matched.sourceWidth || item.sourceWidth,
    sourceDepth: matched.sourceDepth || item.sourceDepth,
    sourceHeight: matched.sourceHeight || item.sourceHeight,
    assetObjectId: matched.assetObjectId
  };
}

function objectAssetDisplayName(item) {
  return String(item?.name || item?.label || 'Objekt').trim() || 'Objekt';
}

function beginObjectAssetPlacement(assetId) {
  const asset = assetLibraryItems().find(item => item.id === assetId) || null;
  if (!asset) return false;
  const ui = ensureUi();
  ui.objectAssetPlacement = {
    asset,
    preview: null,
    project: S.project,
    sheetId: S.drawingSheets?.activeSheetId
  };
  setStatus(`${objectAssetDisplayName(asset)} platzieren`);
  AppRuntime.refresh(false);
  return true;
}

function isObjectAssetPlacementActive() {
  const placement = ensureUi().objectAssetPlacement;
  if (!placement) return false;
  if (placement.project !== S.project || placement.sheetId !== S.drawingSheets?.activeSheetId
    || S.project?.currentView !== 'plan') {
    ensureUi().objectAssetPlacement = null;
    return false;
  }
  return Boolean(placement.asset);
}

function updateObjectAssetPlacementPreview(event) {
  if (!isObjectAssetPlacementActive()) return false;
  ensureUi().objectAssetPlacement.preview = placementPreviewFromEvent(event);
  AppRuntime.requestPlanRender('object-asset-placement-preview');
  return true;
}

function cancelObjectAssetPlacement() {
  const ui = ensureUi();
  if (!ui.objectAssetPlacement) return false;
  ui.objectAssetPlacement = null;
  setStatus('Objektplatzierung abgebrochen');
  AppRuntime.refresh(false);
  return true;
}

function createPlacedObjectAsset(asset, position) {
  const item = normalizeObjectAsset({
    id: makeId('object_asset'),
    type: 'object_asset',
    category: asset.category || 'Objekte',
    x: position.x,
    y: Number.isFinite(Number(asset.defaultFloorOffsetMm)) ? Number(asset.defaultFloorOffsetMm) : 0,
    z: position.z,
    rotation: 0,
    label: asset.label,
    name: asset.name || asset.label,
    color: asset.color || '',
    assetFile: asset.assetFile || '',
    modelPath: asset.modelPath || '',
    planStrokeWidthFactor: asset.planStrokeWidthFactor || 1,
    svgPath: asset.svgPath || '',
    stepPath: asset.stepPath || '',
    dxfPath: asset.dxfPath || '',
    dimensionGeometryPath: asset.dimensionGeometryPath || '',
    dxfEntitiesPath: asset.dxfEntitiesPath || '',
    wireframeDxfPath: asset.wireframeDxfPath || '',
    assetObjectId: asset.assetObjectId,
    articleNumber: asset.articleNumber || '',
    sourceType: asset.sourceType || '',
    width: asset.width,
    height: asset.height,
    depth: asset.depth,
    sourceWidth: asset.sourceWidth,
    sourceDepth: asset.sourceDepth,
    sourceHeight: asset.sourceHeight,
    modelRotationOffsetDeg: asset.modelRotationOffsetDeg
  });
  objectAssets().push(item);
  selectObjectAssets([item.id]);
  return item;
}

function screenWorldFromEvent(event) {
  const svg = getPlanSvg();
  const rect = svg.getBoundingClientRect();
  return screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
}

function placementPreviewFromEvent(event) {
  const world = screenWorldFromEvent(event);
  return {
    x: S.project?.snap ? snap(world.x, S.project.gridSize) : world.x,
    z: S.project?.snap ? snap(world.z, S.project.gridSize) : world.z
  };
}

function handlePlacementClick(event) {
  if (event?.button !== 0 || !isObjectAssetPlacementActive()) return false;
  const placement = ensureUi().objectAssetPlacement;
  const asset = placement?.asset;
  if (!asset) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  recordUndo('Objekt platzieren');
  const item = createPlacedObjectAsset(asset, placementPreviewFromEvent(event));
  ensureUi().objectAssetPlacement = null;
  setStatus(`${objectAssetDisplayName(item)} platziert`);
  AppRuntime.refresh(false);
  return true;
}

function drawObjectAsset(parent, item, options = {}) {
  const selected = options.selected === true;
  const center = worldToScreen(item.x, item.z);
  const { width, depth } = objectAssetDimensions(item);
  const topLeft = worldToScreen(item.x - width / 2, item.z - depth / 2);
  const bottomRight = worldToScreen(item.x + width / 2, item.z + depth / 2);
  const screenWidth = Math.abs(bottomRight.x - topLeft.x);
  const screenHeight = Math.abs(bottomRight.y - topLeft.y);
  const group = svgEl('g', {
    class: `object-asset-plan-item${selected ? ' selected' : ''}`,
    'data-object-asset-id': item.id,
    transform: `rotate(${Number(item.rotation) || 0} ${center.x} ${center.y})`
  }, parent);
  if (item.svgPath) {
    const svgAsset = requestObjectAssetSvg(item.svgPath, item.planStrokeWidthFactor);
    if (svgAsset?.root) {
      drawImportedSvgAsset(parent, svgAsset, {
        className: 'object-asset-inline-svg',
        x: item.x - width / 2,
        z: item.z - depth / 2,
        widthMm: width,
        depthMm: depth,
        // Breite/Tiefe muessen unabhaengig voneinander wirken (Nutzerwunsch
        // 2026-08-18) -- ohne scaleMode:'stretch' skaliert
        // drawImportedSvgAsset() die Tiefe standardmaessig im selben
        // Verhaeltnis wie die Breite (Seitenverhaeltnis gesperrt), wodurch
        // eine Tiefenaenderung sichtbar wirkungslos blieb und eine
        // Breitenaenderung das ganze Objekt gleichmaessig skalierte.
        scaleMode: 'stretch',
        rotationDeg: Number(item.rotation) || 0,
        rotationCenter: { x: item.x, z: item.z },
        opacity: selected ? 0.92 : 0.78,
        pointerEvents: 'all',
        idPrefix: `object-asset-${String(item.id || 'asset').replace(/[^a-zA-Z0-9_-]+/g, '-')}`,
        instanceKey: `object-asset:${String(item.id || 'asset')}`,
        objectAssetId: item.id
      });
    } else {
      drawObjectAssetFallbackShape(group, item, selected, center, screenWidth, screenHeight);
    }
  } else {
    drawObjectAssetFallbackShape(group, item, selected, center, screenWidth, screenHeight);
  }
  // Bewusst KEIN sichtbares/anklickbares Rahmen-Rechteck mehr ueber der
  // vollen Bounding-Box (frueher hier: stroke + pointer-events:'all' auf
  // einem <rect> in Objekt-Groesse) -- das liess bei importierten SVG-
  // Assets eine deutlich groessere Box als die eigentliche Silhouette
  // erscheinen UND machte den gesamten Kasten anklickbar statt nur die
  // sichtbare Geometrie. drawImportedSvgAsset() setzt sein eigenes
  // pointerEvents bereits direkt auf dem SVG-Inhalt (siehe oben), das reicht
  // fuer Auswahl/Interaktion; die Fallback-Box (drawObjectAssetFallbackShape)
  // bleibt unveraendert, da sie selbst die einzige sichtbare Flaeche ist.
  // Kein eigener Dreh-Griff mehr hier -- Objekte werden ueber
  // SystemRegistry.getCoreObjects()/getDrawingObjects() (siehe
  // objectAssetSystem() unten) bereits vom generischen
  // core-group-rotate-handle in selection_controller.js erfasst (derselbe
  // Griff wie fuer Zeichnungsgruppen). Ein zusaetzlicher eigener Griff hier
  // fuehrte zu zwei/drei parallel angezeigten Dreh-Buttons fuer dasselbe
  // Objekt (Nutzer-Meldung 2026-08-18) -- entfernt, EIN Griff genuegt.
}

function drawObjectAssetFallbackShape(targetGroup, objectItem, isSelected, screenCenter, boxWidth, boxHeight) {
  svgEl('rect', {
    x: screenCenter.x - boxWidth / 2,
    y: screenCenter.y - boxHeight / 2,
    width: boxWidth,
    height: boxHeight,
    rx: 3,
    fill: objectItem.color || '#d9e2ec',
    stroke: isSelected ? '#0f766e' : '#64748b',
    'stroke-width': isSelected ? 2 : 1,
    'pointer-events': 'all'
  }, targetGroup);
}

function requestObjectAssetSvg(svgPath, planStrokeWidthFactor = 1) {
  const path = String(svgPath || '').trim();
  if (!path) return null;
  const cached = OBJECT_ASSET_SVG_CACHE.get(path);
  if (cached?.status === 'ready') return cached.asset;
  if (cached?.status === 'loading' || cached?.status === 'failed') return null;
  const entry = { status: 'loading', asset: null };
  OBJECT_ASSET_SVG_CACHE.set(path, entry);
  loadImportedSvgAsset(path, { cache: 'no-store', planStrokeWidthFactor })
    .then(asset => {
      entry.status = 'ready';
      entry.asset = asset;
      AppRuntime.requestAppRefresh(false, 'object-asset-svg-ready');
    })
    .catch(error => {
      entry.status = 'failed';
      console.warn(`Objekt-SVG konnte nicht geladen werden: ${path}`, error);
      AppRuntime.requestAppRefresh(false, 'object-asset-svg-failed');
    });
  return null;
}

// Duenner Wrapper um den systemuebergreifenden Helper in
// dimension_geometry_client.js (Fetch/Cache + Skalierung auf die
// tatsaechliche Objektgroesse/-position). onReady erzeugt die
// Drawing-Objects neu, sobald die Geometrie eintrifft -- ein blosser Repaint
// (requestAppRefresh, passiert bereits im Helper) aktualisiert nur die
// SVG-Darstellung, NICHT die drawing_object_registry-Eintraege, die
// dimension_service.js fuer das Bemassungswerkzeug cached (Registry-Revision
// aendert sich nur bei registerDrawingObject/clearDrawingObjects-Aufrufen) --
// empirisch gefunden: ein reiner requestAppRefresh reichte nicht aus.
function objectAssetDimensionSnapMetadata(item) {
  const { width, depth } = objectAssetDimensions(item);
  return computeDimensionSnapMetadata({
    dimensionGeometryPath: item.dimensionGeometryPath,
    sourceWidth: item.sourceWidth,
    sourceDepth: item.sourceDepth,
    width,
    depth,
    centerX: Number(item.x) || 0,
    centerZ: Number(item.z) || 0,
    onReady: () => createObjectAssetDrawingObjects()
  });
}

function requestObjectAssetDxfEntities(dxfEntitiesPath) {
  const path = String(dxfEntitiesPath || '').trim();
  if (!path) return null;
  const cached = OBJECT_ASSET_DXF_ENTITIES_CACHE.get(path);
  if (cached?.status === 'ready') return cached.entities;
  if (cached?.status === 'loading' || cached?.status === 'failed') return null;
  const entry = { status: 'loading', entities: null };
  OBJECT_ASSET_DXF_ENTITIES_CACHE.set(path, entry);
  const promise = fetch(path, { cache: 'no-store' })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(data => {
      entry.status = 'ready';
      entry.entities = Array.isArray(data?.entities) ? data.entities : [];
    })
    .catch(error => {
      // Erwartet fuer v1.0-Assets/alte v2.0-Konvertierungen ohne diese Datei
      // (404) -- der DXF-Export faellt fuer dieses Objekt dann auf das
      // bisherige Bounding-Box-Rechteck zurueck.
      entry.status = 'failed';
      console.warn(`DXF-Entitaeten konnten nicht geladen werden: ${path}`, error);
    });
  entry.promise = promise;
  return null;
}

// Wartet, bis die DXF-Entitaeten aller uebergebenen Objekte geladen sind
// (oder endgueltig fehlgeschlagen) -- der projektweite DXF-Export ist ein
// einmaliger Download-Klick, kein laufend neu gerendertes UI-Element wie die
// SVG-/Bemassungs-Vorschau, daher reicht "lazy nachladen + irgendwann neu
// zeichnen" hier NICHT: der Nutzer braucht beim ERSTEN Klick bereits die
// vollstaendigen Daten.
async function ensureObjectAssetDxfEntitiesLoaded(items) {
  const pending = [];
  for (const item of items) {
    const path = String(item?.dxfEntitiesPath || '').trim();
    if (!path) continue;
    requestObjectAssetDxfEntities(path);
    const entry = OBJECT_ASSET_DXF_ENTITIES_CACHE.get(path);
    if (entry?.promise) pending.push(entry.promise);
  }
  if (pending.length) await Promise.allSettled(pending);
}

// Skaliert/dreht/verschiebt die STEP-mm-Entitaeten aus dxf_entities.json
// (dieselbe Originalgroesse-Referenz wie objectAssetDimensionSnapMetadata)
// auf die tatsaechliche Platzierung -- fuer den projektweiten DXF-Export
// (js/core/export/dxf_export.js), der Objekt-Assets bisher nur als grobes,
// rotiertes Bounding-Box-Rechteck exportierte (ADR-0001 Abschnitt-3-Befund).
// Liefert null, solange die Geometrie noch nicht geladen ist (Aufrufer muss
// vorher ensureObjectAssetDxfEntitiesLoaded() abwarten) oder das Objekt keine
// dxfEntitiesPath hat -- der Aufrufer faellt dann auf die Bounding Box zurueck.
function objectAssetDxfShapes(item) {
  const entities = requestObjectAssetDxfEntities(item.dxfEntitiesPath);
  if (!entities || !entities.length) return null;
  const { width, depth } = objectAssetDimensions(item);
  const sourceWidth = Number(item.sourceWidth) > 0 ? Number(item.sourceWidth) : width;
  const sourceDepth = Number(item.sourceDepth) > 0 ? Number(item.sourceDepth) : depth;
  const scaleX = sourceWidth > 0 ? width / sourceWidth : 1;
  const scaleZ = sourceDepth > 0 ? depth / sourceDepth : 1;
  // Kreisradien haben keine eigene Achsenrichtung -- bei ungleicher X/Z-
  // Skalierung (Objekt nicht proportional in der Breite/Tiefe verzerrt
  // platziert) wird der Mittelwert verwendet statt eine Ellipse zu
  // konstruieren (matcht die bereits akzeptierte Vereinfachung, mit der auch
  // das platzierte SVG/GLB unter ungleicher Skalierung dargestellt wird).
  const scaleRadius = (scaleX + scaleZ) / 2;
  const center = { x: Number(item.x) || 0, z: Number(item.z) || 0 };
  const rotation = Number(item.rotation) || 0;
  const toWorld = point => rotateWorldPoint(
    { x: center.x + (Number(point?.x) || 0) * scaleX, z: center.z + (Number(point?.z) || 0) * scaleZ },
    center,
    rotation
  );
  const shapes = [];
  for (const entity of entities) {
    if (!entity || typeof entity !== 'object') continue;
    if (entity.type === 'line') {
      const start = toWorld(entity.start);
      const end = toWorld(entity.end);
      shapes.push({ type: 'line', start: { x: start.x, y: start.z }, end: { x: end.x, y: end.z } });
    } else if (entity.type === 'circle') {
      const c = toWorld(entity.center);
      shapes.push({ type: 'circle', center: { x: c.x, y: c.z }, radius: (Number(entity.radius) || 0) * scaleRadius });
    } else if (entity.type === 'arc') {
      const c = toWorld(entity.center);
      shapes.push({
        type: 'arc',
        center: { x: c.x, y: c.z },
        radius: (Number(entity.radius) || 0) * scaleRadius,
        startAngle: (Number(entity.startAngle) || 0) + rotation,
        endAngle: (Number(entity.endAngle) || 0) + rotation
      });
    } else if (entity.type === 'polyline' && Array.isArray(entity.points)) {
      const points = entity.points.map(rawPoint => {
        const world = toWorld(rawPoint);
        return { x: world.x, y: world.z };
      });
      if (points.length >= 2) shapes.push({ type: 'polyline', points, closed: entity.closed === true });
    }
  }
  return shapes;
}

// Gemeinsame Weltposition/-skalierung fuer die projektweiten 3D-Kombi-
// Exporte (Wireframe/STEP) -- beide brauchen dieselben Platzierungsdaten,
// unterscheiden sich nur im transportierten Quelldateipfad.
//
// rotationDeg/modelRotationYDeg: die Server-Combiner (step_combine.py/
// wireframe_combine.py) reproduzieren ueber ihre Ground-Truth-hergeleitete
// Negierung exakt denselben Winkel, der auch an THREE.js' rotation.y
// uebergeben wird (siehe deren Modul-Docstrings) -- NICHT den rohen
// 2D-Ikonen-Winkel. object_asset_render3d.js uebergibt der 3D-Live-Ansicht
// daher -item.rotation als Welt-Rotation (siehe dortige, 2026-08-18 neu
// vermessene Ground-Truth-Herleitung) plus die feste, artikelspezifische
// modelRotationOffsetDeg-Korrektur (siehe object_asset_defaults.js) als
// LOKALE Rotation VOR der Welt-Rotation --
// beides muss hier identisch nachgebildet werden, sonst weicht der Export
// sowohl von 2D als auch von der 3D-Ansicht ab (Nutzer-Meldung 2026-08-18).
function objectAssetPlacementTransform(item) {
  const { width, height, depth } = objectAssetDimensions(item);
  const sourceWidth = Number(item.sourceWidth) > 0 ? Number(item.sourceWidth) : width;
  const sourceHeight = Number(item.sourceHeight) > 0 ? Number(item.sourceHeight) : height;
  const sourceDepth = Number(item.sourceDepth) > 0 ? Number(item.sourceDepth) : depth;
  return {
    x: Number(item.x) || 0,
    y: Number(item.y) || 0,
    z: Number(item.z) || 0,
    rotationDeg: -(Number(item.rotation) || 0),
    modelRotationYDeg: objectAssetModelRotationOffsetRad(item) * 180 / Math.PI,
    scaleX: sourceWidth > 0 ? width / sourceWidth : 1,
    scaleY: sourceHeight > 0 ? height / sourceHeight : 1,
    scaleZ: sourceDepth > 0 ? depth / sourceDepth : 1
  };
}

// Platzierungsparameter (ohne DXF-Text) fuer den projektweiten Wireframe-
// Export (view3d_controller.js exportProjectWireframe()): anders als beim
// 2D-DXF-Export (objectAssetDxfShapes()) wird das eigentliche Kombinieren
// NICHT hier im Client gerechnet, sondern serverseitig im cad-converter
// (wireframe_combine.py, echtes OCS-Handling fuer CIRCLE/ARC beim
// Transformieren) -- dieser Client liefert nur wireframeDxfPath + die
// Weltposition/-skalierung, der Aufrufer holt den rohen DXF-Text separat
// (einmalig, kein Caching noetig, da nur beim Export-Klick gebraucht).
function objectAssetWireframeExportItems() {
  return objectAssets()
    .filter(item => item?.hidden !== true && String(item?.wireframeDxfPath || '').trim())
    .map(item => ({
      label: objectAssetDisplayName(item),
      wireframeDxfPath: item.wireframeDxfPath,
      ...objectAssetPlacementTransform(item)
    }));
}

// Wie objectAssetWireframeExportItems(), aber fuer den projektweiten STEP-
// Export (echte B-Rep-Solids statt DXF-Kurven, siehe step_combine.py) --
// nutzt die ORIGINAL-Quell-STEP-Datei jedes Objekts (item.stepPath).
function objectAssetStepExportItems() {
  return objectAssets()
    .filter(item => item?.hidden !== true && String(item?.stepPath || '').trim())
    .map(item => ({
      label: objectAssetDisplayName(item),
      stepPath: item.stepPath,
      // Fuer die Teilebeschriftung im kombinierten STEP -- faellt auf den
      // Anzeigenamen zurueck, wenn (noch) keine Artikelnummer bekannt ist
      // (z.B. v1.0-Legacy-Objekte).
      articleNumber: String(item.articleNumber || '').trim() || objectAssetDisplayName(item),
      // Objekt-Assets sollen breite/tiefe/hoehe unabhaengig voneinander
      // "stretchen" koennen (Nutzerwunsch 2026-08-18, siehe auch der
      // scaleMode:'stretch'-Fix bei drawImportedSvgAsset()) -- ohne
      // preciseScale mittelt step_combine.py._uniform_scale_matrix()
      // scaleX/Y/Z zu EINEM Faktor (Standardverhalten fuer Artikel, die
      // absichtlich proportional bleiben sollen), was eine reine Breiten-
      // aenderung wieder in eine Gesamt-Skalierung des ganzen Koerpers
      // verwandelt hätte.
      preciseScale: true,
      ...objectAssetPlacementTransform(item)
    }));
}

function loadObjectAssetRender3DModule() {
  // Three.js-Ladepfad bleibt bewusst dynamisch importiert (wie schon bei
  // obstacle_render3d.js/sketch_wall_render3d.js in scene3d.js) — dieses
  // Modul hier wird bereits beim App-Start eager geladen (Kernsystem), ein
  // statischer Import wuerde Three.js entgegen der bestehenden Lazy-Chunk-
  // Strategie ins Hauptbundle ziehen.
  if (!objectAssetRender3DModulePromise) {
    objectAssetRender3DModulePromise = import('./object_asset_render3d.js?v=20260730a');
  }
  return objectAssetRender3DModulePromise;
}

async function renderObjectAssets3D(ctx = {}) {
  const items = objectAssets().filter(item => item?.hidden !== true);
  if (!items.length || !ctx?.THREE) return;
  const render3d = await loadObjectAssetRender3DModule();
  await render3d.buildObjectAssets3D(ctx, items);
}

function renderObjectAssets2D(ctx = {}) {
  const parent = ctx.parentLayer;
  if (!parent) return;
  const selectedIds = new Set(selectedObjectAssetIds());
  for (const item of objectAssets().filter(entry => entry?.hidden !== true)) {
    drawObjectAsset(parent, item, { selected: selectedIds.has(item.id) });
  }
}

function drawObjectAssetInteraction(parent, item) {
  const center = worldToScreen(item.x, item.z), size = objectAssetDimensions(item);
  const scale = S.camera2D.scale;
  svgEl('rect', { x: center.x - size.width * scale / 2, y: center.y - size.depth * scale / 2,
    width: size.width * scale, height: size.depth * scale,
    fill: 'none', stroke: '#f59e0b', 'stroke-width': 2, 'stroke-dasharray': '5 3',
    transform: `rotate(${Number(item.rotation) || 0} ${center.x} ${center.y})`,
    'pointer-events': 'none', 'data-object-asset-id': item.id
  }, parent);
}

function renderObjectAssetOverlay(ctx = {}) {
  if (ctx.options?.gpuInteraction && ctx.parentLayer) {
    const selected = new Set(selectedObjectAssetIds());
    for (const item of objectAssets()) if (selected.has(item.id) && !item.hidden) {
      drawObjectAssetInteraction(ctx.parentLayer, item);
    }
  }
  if (!isObjectAssetPlacementActive()) return;
  const placement = ensureUi().objectAssetPlacement;
  if (!placement.preview || !ctx.parentLayer) return;
  const draw = ctx.options?.gpuInteraction ? drawObjectAssetInteraction : drawObjectAsset;
  const ghostLayer=svgEl('g', {}, ctx.parentLayer);
  draw(ghostLayer, normalizeObjectAsset({
    id: 'object_asset_preview',
    label: objectAssetDisplayName(placement.asset),
    name: placement.asset.name,
    x: placement.preview.x,
    z: placement.preview.z,
    svgPath: placement.asset.svgPath,
    color: placement.asset.color,
    width: placement.asset.width,
    height: placement.asset.height,
    depth: placement.asset.depth
  }), { selected: true });
  applyGhostStyle(ghostLayer);
}

function hitTestObjectAsset(clientX, clientY) {
  const svg = getPlanSvg();
  const rect = svg.getBoundingClientRect();
  const world = screenToWorld(clientX - rect.left, clientY - rect.top);
  for (const item of [...objectAssets()].filter(entry => entry?.hidden !== true).reverse()) {
    if (pointInObjectAsset(world, item)) {
      return {
        kind: 'object-asset',
        id: item.id,
        objectAsset: item,
        point: world
      };
    }
  }
  return null;
}

function handleObjectAssetClick(ctx = {}) {
  if (ensureUi().objectAssetPlacement?.asset) return handlePlacementClick(ctx.event);
  const hit = ctx.hit?.objectAsset ? ctx.hit : hitTestObjectAsset(ctx.event.clientX, ctx.event.clientY);
  if (!hit?.objectAsset) return false;
  selectObjectAssets([hit.objectAsset.id]);
  setStatus(`${objectAssetDisplayName(hit.objectAsset)} markiert`);
  AppRuntime.refresh(false);
  return true;
}

function startObjectAssetMove(ctx = {}) {
  const hit = ctx.hit?.objectAsset ? ctx.hit : hitTestObjectAsset(ctx.event.clientX, ctx.event.clientY);
  if (!hit?.objectAsset) return false;
  const ids = selectedObjectAssetIds().includes(hit.objectAsset.id)
    ? selectedObjectAssetIds()
    : [hit.objectAsset.id];
  selectObjectAssets(ids);
  recordUndo(ids.length > 1 ? 'Objekte verschieben' : 'Objekt verschieben');
  const start = screenWorldFromEvent(ctx.event);
  S.drag = {
    active: true,
    type: 'objectAssetMove',
    systemId: OBJECT_ASSET_SYSTEM_ID,
    startMouse: start,
    hasMoved: false,
    snapshot: createObjectAssetMoveSnapshot(ids),
    lastDx: 0,
    lastDz: 0
  };
  ctx.event.preventDefault?.();
  setStatus(ids.length > 1 ? 'Objekte werden verschoben' : 'Objekt wird verschoben');
  return true;
}

function updateObjectAssetMove(event) {
  if (S.drag?.type !== 'objectAssetMove') return false;
  const world = screenWorldFromEvent(event);
  const dx = world.x - S.drag.startMouse.x;
  const dz = world.z - S.drag.startMouse.z;
  const snappedDx = S.project?.snap ? snap(dx, S.project.gridSize) : dx;
  const snappedDz = S.project?.snap ? snap(dz, S.project.gridSize) : dz;
  S.drag.lastDx = snappedDx;
  S.drag.lastDz = snappedDz;
  if (Math.hypot(snappedDx, snappedDz) > 0.001) S.drag.hasMoved = true;
  applyObjectAssetMoveSnapshot(S.drag.snapshot, snappedDx, snappedDz);
  AppRuntime.requestPlanRender('object-asset-move');
  return true;
}

function finishObjectAssetDrag() {
  if (S.drag?.type !== 'objectAssetMove') return false;
  const moved = S.drag.hasMoved;
  // Ohne dieses Flag bleibt der native `click`, der nach dem Loslassen
  // ohnehin folgt, unterdrückt — sonst trifft er (das Objekt hat sich ja nie
  // "geklickt", nur gezogen) leere Fläche und die generische
  // "Klick-ins-Leere"-Logik wählt das gerade verschobene Objekt sofort
  // wieder ab (Andere Drag-Typen, z.B. obstacles.js, setzen dasselbe Flag
  // aus demselben Grund).
  if (moved) setInteractionFlag('suppressPlanClick', true);
  S.drag.active = false;
  S.drag.type = null;
  S.drag.systemId = null;
  S.drag.snapshot = null;
  setStatus('Bereit');
  AppRuntime.refresh(false);
  return moved;
}

function createObjectAssetMoveSnapshot(ids = selectedObjectAssetIds()) {
  return ids
    .map(id => objectAssetById(id))
    .filter(Boolean)
    .map(item => ({ id: item.id, x: item.x, z: item.z }));
}

function applyObjectAssetMoveSnapshot(snapshot = [], dx = 0, dz = 0) {
  let count = 0;
  for (const entry of snapshot) {
    const item = objectAssetById(entry.id);
    if (!item) continue;
    item.x = entry.x + dx;
    item.z = entry.z + dz;
    count += 1;
  }
  return count;
}

function nudgeObjectAssetSelection(ctx = {}) {
  const ids = selectedObjectAssetIds();
  if (!ids.length) return false;
  recordUndo(ids.length > 1 ? 'Objekte verschieben' : 'Objekt verschieben');
  const dx = Number(ctx.dx) || 0;
  const dz = Number(ctx.dz) || 0;
  for (const id of ids) {
    const item = objectAssetById(id);
    if (!item) continue;
    item.x += dx;
    item.z += dz;
  }
  setStatus(ids.length > 1 ? `${ids.length} Objekte verschoben` : 'Objekt verschoben');
  AppRuntime.refresh(false);
  return true;
}

function createObjectAssetDrawingObjects(ids = null) {
  clearDrawingObjects({ systemType: OBJECT_ASSET_SYSTEM_ID });
  const selectedFilter = Array.isArray(ids) ? new Set(ids) : null;
  return objectAssets()
    .filter(item => item?.hidden !== true)
    .filter(item => !selectedFilter || selectedFilter.has(item.id))
    .map(item => registerDrawingObject({
      id: item.id,
      type: 'object_asset',
      systemType: OBJECT_ASSET_SYSTEM_ID,
      position: { x: item.x, z: item.z },
      rotation: item.rotation,
      selectable: true,
      movable: true,
      rotatable: true,
      scalable: false,
      metadata: {
        groupId: item.id,
        coreGroup: false,
        objectRole: 'object',
        localBounds: objectAssetLocalBounds(item),
        bounds: objectAssetBounds(item),
        label: objectAssetDisplayName(item),
        ...(objectAssetDimensionSnapMetadata(item) || {})
      },
      getState: () => ({
        id: item.id,
        type: 'object_asset',
        systemType: OBJECT_ASSET_SYSTEM_ID,
        position: { x: item.x, z: item.z },
        rotation: item.rotation,
        metadata: {
          groupId: item.id,
          coreGroup: false,
          objectRole: 'object',
          localBounds: objectAssetLocalBounds(item),
          bounds: objectAssetBounds(item),
          label: objectAssetDisplayName(item),
          ...(objectAssetDimensionSnapMetadata(item) || {})
        }
      }),
      setState: state => {
        const target = objectAssetById(item.id);
        if (!target) return false;
        const x = Number(state.position?.x ?? state.x);
        const z = Number(state.position?.z ?? state.z);
        if (Number.isFinite(x)) target.x = x;
        if (Number.isFinite(z)) target.z = z;
        target.rotation = normalizeAngle(Number(state.rotation) || 0);
        return true;
      }
    }))
    .filter(Boolean);
}

function collectObjectAssetBoxSelection(rect, mode = 'window') {
  const ids = objectAssets()
    .filter(item => {
      const bounds = objectAssetBounds(item);
      if (mode === 'crossing') {
        return bounds.maxX >= rect.minX
          && bounds.minX <= rect.maxX
          && bounds.maxZ >= rect.minZ
          && bounds.minZ <= rect.maxZ;
      }
      return bounds.minX >= rect.minX
        && bounds.maxX <= rect.maxX
        && bounds.minZ >= rect.minZ
        && bounds.maxZ <= rect.maxZ;
    })
    .map(item => item.id);
  return { objectAssetIds: ids };
}

function renderSelectedObjectAssetProperties() {
  const item = selectedObjectAsset();
  if (!item) return '';
  return `
    <div class="card object-asset-properties" data-object-asset-properties="${escapeHtml(item.id)}">
      <div class="card-title">${escapeHtml(objectAssetDisplayName(item))}</div>
      <div class="object-asset-property-grid">
        <label>${escapeHtml(t('sidebar_rechts.eigenschaften.label.breite', 'Breite (mm)'))}
          <input id="objectAssetWidthInput" type="number" min="1" step="10" value="${Math.round(item.width)}">
        </label>
        <label>${escapeHtml(t('sidebar_rechts.eigenschaften.label.hoehe', 'Höhe (mm)'))}
          <input id="objectAssetHeightInput" type="number" min="1" step="10" value="${Math.round(item.height)}">
        </label>
        <label>${escapeHtml(t('sidebar_rechts.eigenschaften.label.tiefe', 'Tiefe (mm)'))}
          <input id="objectAssetDepthInput" type="number" min="1" step="10" value="${Math.round(item.depth)}">
        </label>
        <label>${escapeHtml(t('sidebar_rechts.eigenschaften.label.abstand_boden', 'Abstand ab Boden (mm)'))}
          <input id="objectAssetFloorOffsetInput" type="number" min="0" step="10" value="${Math.round(item.y || 0)}">
        </label>
      </div>
      <div class="object-asset-property-actions">
        <button id="btnObjectAssetRotateLeft" class="btn" type="button">${escapeHtml(t('sidebar_rechts.eigenschaften.button.rotieren_links', '90° links'))}</button>
        <button id="btnObjectAssetRotateRight" class="btn" type="button">${escapeHtml(t('sidebar_rechts.eigenschaften.button.rotieren_rechts', '90° rechts'))}</button>
        <button id="btnObjectAssetDelete" class="btn danger" type="button">${escapeHtml(t('sidebar_rechts.eigenschaften.button.loeschen', 'Loeschen'))}</button>
      </div>
    </div>
  `;
}

function bindSelectedObjectAssetProperties() {
  const item = selectedObjectAsset();
  if (!item) return;
  const bindNumber = (id, field, undoLabel, normalize = value => value) => {
    document.getElementById(id)?.addEventListener('change', event => {
      const value = normalize(Number(event.target.value));
      if (!Number.isFinite(value)) return;
      recordUndo(undoLabel);
      updateObjectAsset(item.id, { [field]: value });
    });
  };
  bindNumber('objectAssetWidthInput', 'width', 'Objektbreite aendern', value => Math.max(1, value));
  bindNumber('objectAssetHeightInput', 'height', 'Objekthoehe aendern', value => Math.max(1, value));
  bindNumber('objectAssetDepthInput', 'depth', 'Objekttiefe aendern', value => Math.max(1, value));
  bindNumber('objectAssetFloorOffsetInput', 'y', 'Objektabstand ab Boden aendern', value => Math.max(0, value));
  document.getElementById('btnObjectAssetRotateLeft')?.addEventListener('click', () => {
    recordUndo('Objekt drehen');
    updateObjectAsset(item.id, { rotation: normalizeAngle((Number(item.rotation) || 0) - 90) });
  });
  document.getElementById('btnObjectAssetRotateRight')?.addEventListener('click', () => {
    recordUndo('Objekt drehen');
    updateObjectAsset(item.id, { rotation: normalizeAngle((Number(item.rotation) || 0) + 90) });
  });
  document.getElementById('btnObjectAssetDelete')?.addEventListener('click', () => {
    recordUndo('Objekt loeschen');
    removeObjectAssets([item.id]);
    AppRuntime.refresh(false);
  });
}

function objectAssetSystem() {
  return {
    id: OBJECT_ASSET_SYSTEM_ID,
    categoryId: '__core_hidden__',
    companyId: '__core__',
    label: 'Objekte',
    meta: {
      globalSystem: true,
      hiddenFromNavigation: true,
      omitFromBom: true,
      planLayerId: 'objects.default'
    },
    multiSelectionFields: ['objectAssetIds'],
    resolveItemToMultiSelection(item) {
      if (item.kind === 'object_asset' && item.id) return { field: 'objectAssetIds', id: item.id };
      return null;
    },
    resolveHitToMultiSelection(hit) {
      const id = hit.objectAsset?.id || hit.id;
      return id ? { field: 'objectAssetIds', id } : null;
    },
    mergeLegacySelection(resolved, sel) {
      if (Array.isArray(sel?.objectAssetIds)) resolved.objectAssetIds.push(...sel.objectAssetIds);
      if (sel?.objectAssetId) resolved.objectAssetIds.push(sel.objectAssetId);
    },
    syncLegacySelectionFields(selection, S_selection) {
      S_selection.objectAssetId = selection.objectAssetIds?.at(-1) || null;
      S_selection.objectAssetIds = [...(selection.objectAssetIds || [])];
      S_selection.items = (S_selection.items || []).filter(item => item.owner !== OBJECT_ASSET_SYSTEM_ID);
      for (const id of selection.objectAssetIds || []) {
        if (id) S_selection.items = [...S_selection.items, { owner: OBJECT_ASSET_SYSTEM_ID, kind: 'object_asset', id }];
      }
    },
    selectionPriority: 850,
    render2D: renderObjectAssets2D,
    render2DOverlay: renderObjectAssetOverlay,
    render3D: renderObjectAssets3D,
    hitTest2D(clientX, clientY) {
      return hitTestObjectAsset(clientX, clientY);
    },
    ownsDomTarget(target) {
      return Boolean(target?.closest?.('[data-object-asset-id]'));
    },
    wantsClickBeforeHitTest() {
      return Boolean(ensureUi().objectAssetPlacement?.asset);
    },
    handleClick: handleObjectAssetClick,
    // Drehen laeuft nicht mehr ueber dieses System, sondern ausschliesslich
    // ueber den generischen core-group-rotate-handle in
    // selection_controller.js (via getCoreObjects()/getDrawingObjects()
    // unten) -- derselbe Griff wie fuer Zeichnungsgruppen. Ein frueherer
    // eigener Dreh-Griff hier fuehrte zu mehreren parallel angezeigten
    // Dreh-Buttons fuer dasselbe Objekt (Nutzer-Meldung 2026-08-18).
    wantsPointerDownBeforeSelection() {
      return false;
    },
    handlePointerDown(ctx = {}) {
      return startObjectAssetMove(ctx);
    },
    handlePointerMove(ctx = {}) {
      if (S.drag?.type === 'objectAssetMove') return updateObjectAssetMove(ctx.event);
      if (ensureUi().objectAssetPlacement?.asset) {
        ensureUi().objectAssetPlacement.preview = placementPreviewFromEvent(ctx.event);
        AppRuntime.requestPlanRender('object-asset-placement-preview');
        return true;
      }
      return false;
    },
    handlePointerUp() {
      return finishObjectAssetDrag();
    },
    wantsPointerMoveFrame() {
      return Boolean(ensureUi().objectAssetPlacement?.asset)
        || S.drag?.type === 'objectAssetMove';
    },
    clearPointerPreview() {
      if (!ensureUi().objectAssetPlacement?.preview) return false;
      ensureUi().objectAssetPlacement.preview = null;
      return true;
    },
    cancelInteraction() {
      return cancelObjectAssetPlacement();
    },
    clearSelection: clearObjectAssetSelection,
    deleteSelection() {
      const ids = selectedObjectAssetIds();
      if (!ids.length) return false;
      recordUndo(ids.length > 1 ? 'Objekte loeschen' : 'Objekt loeschen');
      removeObjectAssets(ids);
      AppRuntime.refresh(false);
      return true;
    },
    nudgeSelection: nudgeObjectAssetSelection,
    deleteObjects(ctx = {}) {
      const ids = ctx.selection?.objectAssetIds || [];
      return removeObjectAssets(ids);
    },
    hitBelongsToSelection(hit, selection = {}) {
      return hit?.kind === 'object-asset' && (selection.objectAssetIds || []).includes(hit.id);
    },
    createMoveSnapshot(ctx = {}) {
      const ids = ctx.selection?.objectAssetIds || [];
      const items = ids.map(id => objectAssetById(id)).filter(Boolean);
      return items.length ? { objectAssetIds: items.map(item => item.id), items: createObjectAssetMoveSnapshot(items.map(item => item.id)), count: items.length } : null;
    },
    applyMoveSnapshot(ctx = {}) {
      return applyObjectAssetMoveSnapshot(ctx.snapshot?.items || [], ctx.snappedDx ?? ctx.dx ?? 0, ctx.snappedDz ?? ctx.dz ?? 0);
    },
    syncMoveSelection(ctx = {}) {
      return { objectAssetIds: ctx.snapshot?.objectAssetIds || [] };
    },
    getCoreObjects() {
      return getObjectAssetCoreObjects();
    },
    selectCoreObject(ctx = {}) {
      const ids = [ctx.object?.objectId || ctx.object?.id].filter(Boolean);
      return selectObjectAssets(ids);
    },
    setCoreObjectVisibility(ctx = {}) {
      const id = ctx.object?.objectId || ctx.object?.id;
      const item = objectAssetById(id);
      if (!item) return false;
      recordUndo(ctx.visible === false ? 'Objekt ausblenden' : 'Objekt einblenden');
      item.hidden = ctx.visible === false;
      if (item.hidden && selectedObjectAssetIds().includes(item.id)) clearObjectAssetSelection();
      return true;
    },
    getDrawingObjects() {
      return createObjectAssetDrawingObjects();
    },
    collectBoxSelection: collectObjectAssetBoxSelection,
    selectBoxSelection(ctx = {}) {
      const ids = ctx.selection?.objectAssetIds || [];
      if (!ids.length) return false;
      return selectObjectAssets(ctx.multiple ? ids : [ids[0]], { clearForeign: false });
    },
    getProjectBounds() {
      return objectAssets().filter(item => item?.hidden !== true).map(objectAssetBounds);
    },
    getProjectObjects() {
      return objectAssets().filter(item => item?.hidden !== true).map(item => ({
        id: item.id,
        type: item.type,
        systemId: OBJECT_ASSET_SYSTEM_ID,
        category: item.category,
        x: item.x,
        y: item.y,
        z: item.z,
        rotation: item.rotation,
        width: item.width,
        height: item.height,
        depth: item.depth,
        modelRotationOffsetDeg: item.modelRotationOffsetDeg,
        placementDefaultsVersion: item.placementDefaultsVersion,
        bounds: objectAssetBounds(item),
        label: objectAssetDisplayName(item),
        name: item.name,
        assetFile: item.assetFile,
        modelPath: item.modelPath,
        svgPath: item.svgPath,
        dxfShapes: objectAssetDxfShapes(item),
        omitFromBom: true
      }));
    },
    getWireframeExportItems: objectAssetWireframeExportItems,
    getStepExportItems: objectAssetStepExportItems,
    ensureDxfShapesReady: ensureObjectAssetDxfEntitiesReady
  };
}

// Fuer js/core/export/dxf_export.js: laedt die DXF-Entitaeten aller aktuell
// platzierten Objekt-Assets vor, damit getProjectObjects() beim naechsten
// Aufruf bereits die vollstaendigen dxfShapes liefert (siehe
// ensureObjectAssetDxfEntitiesLoaded()-Kommentar -- ein einmaliger Download-
// Klick darf nicht auf den ersten Klick unvollstaendige Daten liefern).
async function ensureObjectAssetDxfEntitiesReady() {
  await ensureObjectAssetDxfEntitiesLoaded(objectAssets());
}

function registerCoreObjectAssetSystem(registry, projectStateService) {
  projectStateService?.registerProjectNormalizer?.(OBJECT_ASSET_SYSTEM_ID, ({ project }) => {
    ensureObjectAssets(project);
    ensureSelection();
    const validIds = new Set(objectAssets().map(item => item.id));
    if (S.selection.objectAssetId && !validIds.has(S.selection.objectAssetId)) S.selection.objectAssetId = null;
    S.selection.objectAssetIds = (S.selection.objectAssetIds || []).filter(id => validIds.has(id));
    if (Array.isArray(S.selection.items)) {
      S.selection.items = S.selection.items.filter(item =>
        item.owner !== OBJECT_ASSET_SYSTEM_ID || validIds.has(item.id)
      );
    }
  }, { priority: 120 });
  SelectionService.registerLegacySelectionHandler(OBJECT_ASSET_SYSTEM_ID, item => {
    if (item?.kind !== 'object_asset' || !objectAssetById(item.id)) return false;
    selectObjectAssets([item.id]);
    return true;
  });
  SelectionService.registerLegacySelectionClearHandler(OBJECT_ASSET_SYSTEM_ID, () => {
    clearObjectAssetSelection();
    return true;
  });
  SelectionService.registerLegacySelectionCollector(OBJECT_ASSET_SYSTEM_ID, {
    priority: 500,
    collectMulti(_legacy, context = {}) {
      return (context.items || []).filter(item => item.owner === OBJECT_ASSET_SYSTEM_ID && item.kind === 'object_asset');
    },
    collectPrimary(legacy) {
      return legacy?.objectAssetId
        ? { owner: OBJECT_ASSET_SYSTEM_ID, kind: 'object_asset', id: legacy.objectAssetId }
        : null;
    }
  });
  registry.registerSystem(objectAssetSystem());
  refreshObjectAssetLibrary();
}

export {
  isObjectAssetPlacementActive,
  updateObjectAssetPlacementPreview,
  handlePlacementClick,
  OBJECT_ASSET_SYSTEM_ID,
  assetLibraryItems,
  beginObjectAssetPlacement,
  bindSelectedObjectAssetProperties,
  cancelObjectAssetPlacement,
  ensureObjectAssetDxfEntitiesReady,
  objectAssetById,
  objectAssetLibraryError,
  objectAssets,
  objectAssetStepExportItems,
  objectAssetWireframeExportItems,
  migrateObjectAssetToV2Library,
  normalizeAssetObjectV2LibraryItem,
  normalizeObjectAsset,
  refreshObjectAssetLibrary,
  registerCoreObjectAssetSystem,
  renderSelectedObjectAssetProperties,
  selectedObjectAsset,
  selectObjectAssets,
  updateObjectAsset
};
